import styled from 'styled-components';
import Button from '../../../components/atoms/Button';
import { mq } from '../../../theme/mediaQueries';

export const StyledSignInRoot = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.05fr);
  min-height: 100vh;
  width: 100%;

  ${mq.ltMd} { grid-template-columns: 1fr; }
`;

export const StyledSignInBrand = styled.div`
  background: var(--brand-ink);
  color: var(--surface);
  padding: var(--s-8) 48px;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 100vh;

  ${mq.ltMd} { display: none; }
`;

export const StyledSignInBrandTop = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
  z-index: 1;
`;

export const StyledSignInBrandLogoText = styled.div`
  line-height: 1.1;
`;

export const StyledSignInBrandLogoTitle = styled.div`
  font-weight: 800;
  font-size: 15px;
  letter-spacing: 0.14em;
  color: var(--surface);
`;

export const StyledSignInBrandLogoSub = styled.div`
  font-size: 12px;
  color: rgba(255,255,255,.55);
  margin-top: 2px;
  letter-spacing: 0.04em;
`;

export const StyledSignInBrandPitch = styled.div`
  position: relative;
  z-index: 1;
  max-width: 520px;
`;

export const StyledSignInBrandEyebrow = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--brand-lime);
  margin-bottom: 18px;
`;

export const StyledSignInBrandHeading = styled.h1`
  margin: 0;
  font-size: var(--t-5xl, 2.5rem);
  font-weight: 700;
  letter-spacing: -0.025em;
  line-height: 1.05;
  color: var(--surface);
`;

export const StyledSignInBrandDesc = styled.p`
  margin-top: 18px;
  color: rgba(255,255,255,.7);
  font-size: var(--t-lg, 1.0625rem);
  line-height: 1.5;
  max-width: 460px;
`;

export const StyledSignInBrandFeatures = styled.ul`
  list-style: none;
  margin: 36px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const StyledSignInBrandFeature = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  color: rgba(255, 255, 255, 0.82);
  font-size: var(--t-base, 1rem);
  font-weight: 500;

  &::before {
    content: '✓';
    flex: none;
    width: 22px;
    height: 22px;
    display: grid;
    place-items: center;
    border-radius: 50%;
    background: rgba(197, 232, 97, 0.16);
    color: var(--brand-lime);
    font-size: 12px;
    font-weight: 700;
  }
`;

export const StyledSignInBrandFooter = styled.div`
  position: relative;
  z-index: 1;
  color: rgba(255,255,255,.4);
  font-size: var(--t-xs);
  display: flex;
  gap: 18px;

  a {
    color: inherit;
    text-decoration: none;
    &:hover { color: rgba(255,255,255,.7); }
  }
`;

export const StyledSignInFormPane = styled.div`
  display: grid;
  place-items: center;
  padding: var(--s-8);
  background: var(--paper);
  position: relative;
`;

export const StyledSignInLangSwitch = styled.div`
  position: absolute;
  top: var(--s-6);
  inset-inline-end: var(--s-6);
  display: inline-flex;
  gap: 2px;
  padding: 3px;
  border: 1px solid var(--line);
  border-radius: var(--r-pill, 999px);
  background: var(--surface);
`;

export const StyledSignInLangButton = styled.button<{ active: boolean }>`
  appearance: none;
  border: none;
  cursor: pointer;
  padding: 5px 12px;
  border-radius: var(--r-pill, 999px);
  font-size: var(--t-sm);
  font-weight: 600;
  line-height: 1;
  transition: background 0.15s ease, color 0.15s ease;
  background: ${({ active }) => (active ? 'var(--brand-ink)' : 'transparent')};
  color: ${({ active }) => (active ? 'var(--surface)' : 'var(--muted)')};

  &:hover {
    color: ${({ active }) => (active ? 'var(--surface)' : 'var(--ink)')};
  }
`;

export const StyledSignInFormBox = styled.div`
  width: min(420px, 100%);
`;

export const StyledSignInEyebrow = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--muted);
  margin-bottom: 10px;
`;

export const StyledSignInHeading = styled.h1`
  margin: 0 0 8px;
  font-size: var(--t-3xl);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.1;
  color: var(--ink);
`;

export const StyledSignInSubtext = styled.p`
  margin: 0 0 32px;
  font-size: var(--t-base);
  color: var(--muted);

  a {
    color: var(--brand-green);
    font-weight: 600;
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }
`;

export const StyledSignInFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

export const StyledSignInActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 4px;
`;

export const StyledSignInForgot = styled.a`
  font-size: var(--t-sm);
  color: var(--brand-green);
  font-weight: 600;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

export const SignInButton = styled(Button)`
  width: 100%;
  justify-content: center;

  &:disabled,
  &[disabled] {
    background: var(--paper-2);
    color: var(--muted-2);
    opacity: 1;
    cursor: not-allowed;
    pointer-events: auto;
  }
`;

export const StyledSignInDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--muted-2);
  font-size: var(--t-sm);
  margin-top: 4px;
`;

export const StyledSignInDividerLine = styled.div`
  flex: 1;
  height: 1px;
  background: var(--line);
`;

export const StyledSignInFootnote = styled.p`
  margin: var(--s-6) 0 0;
  font-size: var(--t-sm);
  color: var(--muted);
  text-align: center;

  a {
    color: var(--brand-green);
    font-weight: 600;
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }
`;
